import { Button, Card, Input, Modal, Spin, Typography, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import React, { useEffect, useState, useRef } from 'react';
import { DemoDetailsModalProps } from '../../libs/types/src';

const { Title, Text } = Typography;

const DemoDetailsModal: React.FC<DemoDetailsModalProps> = ({
    open,
    onCancel,
    selectedDemo,
    demoDetails,
    demoDetailsLoading,
    updateAdditionalContextLoading,
    onUpdateAdditionalContext
}) => {
    const [additionalContext, setAdditionalContext] = useState('');
    const [videoError, setVideoError] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        setAdditionalContext(demoDetails?.additional_info || '');
        setVideoError(false);
    }, [demoDetails?.additional_info]);

    const handleSave = () => {
        if (selectedDemo?.id) {
            Modal.confirm({
                title: 'Confirm Save',
                content: 'Are you sure you want to save the additional information?',
                onOk: () => {
                    onUpdateAdditionalContext(selectedDemo.id, additionalContext);
                    message.success('Additional information saved successfully');
                },
                onCancel: () => {
                    // User cancelled the save operation
                }
            });
        }
    };

    const handleStopVideo = () => {
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
        setVideoError(false);
        onCancel();
    };

    const handleVideoError = () => {
        setVideoError(true);
    };

    const handleDownload = () => {
        if (selectedDemo?.attachment_url) {
            const link = document.createElement('a');
            link.href = selectedDemo.attachment_url;
            link.download = selectedDemo.file_name || 'demo-video';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={handleStopVideo}
            title={selectedDemo ? (selectedDemo.file_name || 'Demo Details') : ''}
            footer={null}
            width={900}
            className="demos-modal"
            closeIcon={<span className="demos-modal-close">×</span>}
        >
            {selectedDemo && (
                <div className="demos-modal-content">
                    <Spin spinning={demoDetailsLoading} tip="Loading demo details...">
                        {/* Video Preview */}
                        {!videoError ? (
                            <video 
                                src={selectedDemo.attachment_url} 
                                className="demos-modal-video-preview" 
                                preload='none' 
                                controls
                                ref={videoRef}
                                onError={handleVideoError}
                            />
                        ) : (
                            <div className="demos-modal-video-error">
                                <div className="demos-modal-video-error-content">
                                    <Text type="secondary" className="demos-modal-video-error-message">
                                        The video format may not be supported by your browser. You can download it to view locally.
                                    </Text>
                                    <Button 
                                        type="primary" 
                                        icon={<DownloadOutlined />}
                                        onClick={handleDownload}
                                        className="demos-modal-video-error-download"
                                    >
                                        Download Video
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* AI-Generated Analysis Section */}
                        <div className="demos-modal-analysis-section">
                            <Title level={5} className="demos-modal-analysis-title">
                                <span>💡</span> AI-Generated Analysis
                            </Title>
                            <div className="demos-modal-analysis-box">
                                {demoDetails ? (
                                    <>
                                        <div className="demos-modal-analysis-overview">
                                            <Text strong>Overview</Text>
                                            <div>
                                                <Text>
                                                    {demoDetails.video_analysis?.product_analysis?.product_description || 'No overview available'}
                                                </Text>
                                            </div>
                                        </div>
                                        
                                        <div className="demos-modal-analysis-features">
                                            <Text strong>Key Features Identified</Text>
                                            <ul>
                                                {demoDetails.video_analysis?.product_analysis?.unique_features && 
                                                 demoDetails.video_analysis?.product_analysis?.unique_features.length > 0 ? (
                                                    demoDetails.video_analysis?.product_analysis?.unique_features.map((feature: any, index: number) => (
                                                        <li key={index}>
                                                            <Text>{feature.name}</Text><br />
                                                            <Text type="secondary">{feature.description}</Text>
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li><Text>No features identified yet</Text></li>
                                                )}
                                            </ul>
                                        </div>
                                        
                                        <Card className="demos-modal-analysis-card">
                                            <Text strong>Target Audience</Text>
                                            <div>
                                                <Text>
                                                    {demoDetails.video_analysis?.product_analysis?.target_audience.join(', ') || 'Not specified'}
                                                </Text>
                                            </div>
                                        </Card>

                                        <div className="demos-modal-analysis-features">
                                            <Text strong>Unique Pain Points</Text>
                                            <ul>
                                                {demoDetails.video_analysis?.product_analysis?.core_pain_points && 
                                                 demoDetails.video_analysis?.product_analysis?.core_pain_points.length > 0 ? (
                                                    demoDetails.video_analysis?.product_analysis?.core_pain_points.map((feature: string, index: number) => (
                                                        <li key={index}>
                                                            <Text>{feature}</Text>
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li><Text>No pain points identified yet</Text></li>
                                                )}
                                            </ul>
                                        </div>

                                        <div className="demos-modal-analysis-features">
                                            <Text strong>Additional Insights</Text>
                                            <ul>
                                                {demoDetails.video_analysis?.product_analysis?.additional_insights && 
                                                 demoDetails.video_analysis?.product_analysis?.additional_insights.length > 0 ? (
                                                    demoDetails.video_analysis?.product_analysis?.additional_insights.map((feature: string, index: number) => (
                                                        <li key={index}>
                                                            <Text>{feature}</Text>
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li><Text>No additional insights identified yet</Text></li>
                                                )}
                                            </ul>
                                        </div>

                                        <div className="demos-modal-analysis-features">
                                            <Text strong>Use Cases</Text>
                                            <ul>
                                                {demoDetails.video_analysis?.product_analysis?.use_cases && 
                                                 demoDetails.video_analysis?.product_analysis?.use_cases.length > 0 ? (
                                                    demoDetails.video_analysis?.product_analysis?.use_cases.map((feature: any, index: number) => (
                                                        <li key={index}>
                                                            <Text>{feature.scenario}</Text><br />
                                                            <Text type="secondary">Target Users: {feature.target_users.join(', ')}</Text>
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li><Text>No use cases identified yet</Text></li>
                                                )}
                                            </ul>
                                        </div>
                                    </>
                                ) : (
                                    <div className="demos-modal-analysis-overview">
                                        <Text strong>Overview</Text>
                                        <div>
                                            <Text>
                                                AI analysis is being generated for this video. The system is identifying key features and creating insights.
                                            </Text>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Additional Information Section */}
                            <div className="demos-modal-additional-info">
                                <Title level={5}>Additional Information</Title>
                                <Input.TextArea 
                                    placeholder="Any additional context for the sales team..." 
                                    rows={6} 
                                    className="demos-modal-textarea" 
                                    value={additionalContext} 
                                    onChange={(e) => setAdditionalContext(e.target.value)} 
                                />
                                <div className="demos-modal-actions">
                                    <Button onClick={onCancel}>Cancel</Button>
                                    <Button 
                                        type="primary" 
                                        disabled={additionalContext === demoDetails?.additional_info} 
                                        loading={updateAdditionalContextLoading} 
                                        onClick={handleSave}
                                    >
                                        Save Details
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Spin>
                </div>
            )}
        </Modal>
    );
};

export default DemoDetailsModal; 